import React, { useState, useEffect, useCallback } from 'react';
import {
  Car,
  Plus,
  Search,
  MoreVertical,
  Edit2,
  Trash2,
  RefreshCw,
  Wifi,
  WifiOff,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { apiService, type AdminVehicleRecord, type CreateAdminVehicleRequest, type AdminCardRecord, type AdminCompanyRecord } from '../../services/api';
import Modal from '../ui/Modal';
import './VehicleManagementPage.css';

// ---------- Car manufacturers (simplified list, mirrors client portal) ----------
const CAR_MANUFACTURERS: Record<string, string[]> = {
  Toyota:       ['Corolla', 'Camry', 'Hilux', 'Hiace', 'Land Cruiser', 'RAV4', 'Fortuner', 'Prado', 'Venza', 'Yaris'],
  Honda:        ['Civic', 'Accord', 'CR-V', 'Pilot', 'HR-V', 'City', 'Odyssey', 'Fit'],
  'Mercedes-Benz': ['C-Class', 'E-Class', 'S-Class', 'GLE-Class', 'GLC-Class', 'G-Wagon', 'CLA-Class'],
  Lexus:        ['ES 350', 'IS 250', 'RX 350', 'GX 460', 'LX 570', 'LX 600', 'NX 200t'],
  Hyundai:      ['Elantra', 'Sonata', 'Tucson', 'Santa Fe', 'Accent', 'Ioniq', 'Palisade'],
  Kia:          ['Rio', 'Cerato', 'Optima', 'Sportage', 'Sorento', 'Picanto', 'Telluride'],
  Nissan:       ['Almera', 'Altima', 'Pathfinder', 'Patrol', 'X-Trail', 'Sentra', 'Maxima'],
  Ford:         ['Focus', 'Fusion', 'Explorer', 'Edge', 'Ranger', 'F-150', 'Mustang', 'Escape'],
  Volkswagen:   ['Golf', 'Passat', 'Tiguan', 'Touareg', 'Jetta', 'Atlas', 'Polo'],
  'Land Rover': ['Range Rover', 'Defender', 'Discovery', 'Freelander'],
  Chevrolet:    ['Blazer', 'Cruze', 'Malibu', 'Equinox', 'Silverado', 'Suburban', 'Tahoe'],
  BMW:          ['3 Series', '5 Series', '7 Series', 'X3', 'X5', 'X6', 'M3', 'M5'],
  Audi:         ['A3', 'A4', 'A6', 'Q5', 'Q7', 'Q8', 'TT'],
  Others:       ['Custom Model'],
};
const MANUFACTURERS = Object.keys(CAR_MANUFACTURERS);

// ---------- Blank form state ----------
const blankForm = (): CreateAdminVehicleRequest => ({
  companyId: '',
  cardId: '',
  plateNumber: '',
  make: '',
  model: '',
  year: new Date().getFullYear(),
  vin: '',
  imei: '',
  contractId: '',
  department: '',
  assignedDriver: '',
  tankCapacity: 0,
  baseOdometer: 0,
  fuelBaseline: 0,
  iccid: '',
  trackerModel: '',
  useVirtualFuelTracking: false,
});

const PAGE_SIZE = 15;

const getPaginationItems = (currentPage: number, total: number): (number | 'ellipsis')[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  if (currentPage <= 4) {
    return [1, 2, 3, 4, 5, 'ellipsis', total];
  }
  if (currentPage >= total - 3) {
    return [1, 'ellipsis', total - 4, total - 3, total - 2, total - 1, total];
  }
  return [1, 'ellipsis', currentPage - 1, currentPage, currentPage + 1, 'ellipsis', total];
};

// ---------- Action Dropdown ----------
interface ActionDropdownProps {
  vehicle: AdminVehicleRecord;
  onEdit: (v: AdminVehicleRecord) => void;
  onDelete: (v: AdminVehicleRecord) => void;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({ vehicle, onEdit, onDelete }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="vehicle-action-dropdown">
      <button
        className="vehicle-action-trigger"
        id={`veh-action-${vehicle.id}`}
        onClick={() => setOpen(!open)}
        aria-label="Vehicle actions"
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <>
          <div className="dropdown-overlay" onClick={() => setOpen(false)} />
          <div className="vehicle-dropdown-menu">
            <button
              id={`veh-edit-${vehicle.id}`}
              className="vehicle-dropdown-item"
              onClick={() => { setOpen(false); onEdit(vehicle); }}
            >
              <Edit2 size={14} /> Edit Vehicle
            </button>
            <button
              id={`veh-delete-${vehicle.id}`}
              className="vehicle-dropdown-item danger"
              onClick={() => { setOpen(false); onDelete(vehicle); }}
            >
              <Trash2 size={14} /> Delete Vehicle
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ---------- Main Component ----------
const VehicleManagementPage: React.FC = () => {
  const [vehicles, setVehicles] = useState<AdminVehicleRecord[]>([]);
  const [companies, setCompanies] = useState<AdminCompanyRecord[]>([]);
  const [unassignedCards, setUnassignedCards] = useState<AdminCardRecord[]>([]);

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCompanyId, setFilterCompanyId] = useState('');
  const [page, setPage] = useState(1);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<AdminVehicleRecord | null>(null);

  // Form state
  const [form, setForm] = useState<CreateAdminVehicleRequest>(blankForm());
  const [customMake, setCustomMake] = useState('');
  const [customModel, setCustomModel] = useState('');

  // Available model options based on selected make
  const modelOptions = form.make && CAR_MANUFACTURERS[form.make] ? CAR_MANUFACTURERS[form.make] : [];
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 1979 }, (_, i) => currentYear - i);

  // ---- Load data ----
  const loadVehicles = useCallback(async (companyId?: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getAdminVehicles(companyId || undefined);
      setVehicles(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load vehicles');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadCompanies = useCallback(async () => {
    try {
      const data = await apiService.getAdminCompanies();
      setCompanies(Array.isArray(data) ? data : []);
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    loadVehicles();
    loadCompanies();
  }, [loadVehicles, loadCompanies]);

  // Load unassigned cards when companyId changes in the form
  useEffect(() => {
    if (!form.companyId) { setUnassignedCards([]); return; }
    apiService.getUnassignedCardsForCompany(form.companyId)
      .then(cards => setUnassignedCards(cards))
      .catch(() => setUnassignedCards([]));
  }, [form.companyId]);

  // Re-filter vehicles when company filter changes
  useEffect(() => {
    loadVehicles(filterCompanyId);
    setPage(1);
  }, [filterCompanyId, loadVehicles]);

  // ---- Filtering & Pagination ----
  const q = searchQuery.toLowerCase();
  const filtered = vehicles.filter(v =>
    !q ||
    (v.plateNumber || '').toLowerCase().includes(q) ||
    (v.make || '').toLowerCase().includes(q) ||
    (v.model || '').toLowerCase().includes(q) ||
    (v.assignedDriver || '').toLowerCase().includes(q) ||
    (v.vin || '').toLowerCase().includes(q) ||
    (v.imei || '').toLowerCase().includes(q) ||
    (v.company?.legalName || companies.find(c => c.id === v.companyId)?.legalName || '').toLowerCase().includes(q)
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ---- Form helpers ----
  const setField = (key: keyof CreateAdminVehicleRequest, value: any) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const resetForm = () => {
    setForm(blankForm());
    setCustomMake('');
    setCustomModel('');
    setUnassignedCards([]);
  };

  // ---- Add Vehicle ----
  const handleOpenAdd = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.companyId) { alert('Please select a company'); return; }
    if (!form.cardId) { alert('Please select a fuel card'); return; }

    const finalMake  = form.make === 'Others' ? customMake : form.make;
    const finalModel = form.model === 'Custom Model' ? customModel : form.model;

    try {
      setIsSubmitting(true);
      await apiService.createAdminVehicle({
        ...form,
        make: finalMake,
        model: finalModel,
        year: Number(form.year),
        tankCapacity: Number(form.tankCapacity),
        baseOdometer: Number(form.baseOdometer),
        fuelBaseline: Number(form.fuelBaseline),
        ...(form.imei?.trim() ? { imei: form.imei.trim() } : { imei: undefined }),
      });
      setShowAddModal(false);
      resetForm();
      await loadVehicles(filterCompanyId);
    } catch (err: any) {
      alert(err?.message || 'Failed to create vehicle');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Edit Vehicle ----
  const handleOpenEdit = (v: AdminVehicleRecord) => {
    setSelectedVehicle(v);
    const attachedCardId = v.cardId || (v.cards && v.cards.length > 0 ? v.cards[0].id : '');
    setForm({
      companyId: v.companyId,
      cardId: attachedCardId,
      plateNumber: v.plateNumber || '',
      make: v.make || '',
      model: v.model || '',
      year: v.year || currentYear,
      vin: v.vin || '',
      imei: v.imei || '',
      contractId: v.contractId || '',
      department: v.department || '',
      assignedDriver: v.assignedDriver || '',
      tankCapacity: v.tankCapacity || 0,
      baseOdometer: v.baseOdometer || 0,
      fuelBaseline: v.fuelBaseline || 0,
      iccid: v.iccid || '',
      trackerModel: v.trackerModel || '',
      useVirtualFuelTracking: v.useVirtualFuelTracking || false,
    });
    setCustomMake('');
    setCustomModel('');
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedVehicle) return;
    const finalMake  = form.make === 'Others' ? customMake : form.make;
    const finalModel = form.model === 'Custom Model' ? customModel : form.model;

    try {
      setIsSubmitting(true);
      await apiService.updateAdminVehicle(selectedVehicle.id, {
        plateNumber: form.plateNumber,
        make: finalMake,
        model: finalModel,
        year: Number(form.year),
        vin: form.vin,
        contractId: form.contractId,
        department: form.department,
        assignedDriver: form.assignedDriver,
        tankCapacity: Number(form.tankCapacity),
        baseOdometer: Number(form.baseOdometer),
        fuelBaseline: Number(form.fuelBaseline),
        iccid: form.iccid,
        trackerModel: form.trackerModel,
        useVirtualFuelTracking: form.useVirtualFuelTracking,
        ...(form.imei?.trim() ? { imei: form.imei.trim() } : {}),
      });
      setShowEditModal(false);
      setSelectedVehicle(null);
      resetForm();
      await loadVehicles(filterCompanyId);
    } catch (err: any) {
      alert(err?.message || 'Failed to update vehicle');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Delete Vehicle ----
  const handleOpenDelete = (v: AdminVehicleRecord) => {
    setSelectedVehicle(v);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedVehicle) return;
    try {
      setIsSubmitting(true);
      await apiService.deleteAdminVehicle(selectedVehicle.id);
      setShowDeleteModal(false);
      setSelectedVehicle(null);
      await loadVehicles(filterCompanyId);
    } catch (err: any) {
      alert(err?.message || 'Failed to delete vehicle');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---- Stats (dynamically reflects current company filter & search) ----
  const totalVehicles = filtered.length;
  const withIot = filtered.filter(v => Boolean(v.imei && v.imei.trim() !== '')).length;
  const withCard = filtered.filter(v => Boolean(v.cardId || v.cardReference || (v.cards && v.cards.length > 0))).length;
  const companies_count = new Set(filtered.map(v => v.companyId).filter(Boolean)).size;

  // ---- Vehicle form body (shared between add and edit) ----
  const renderVehicleForm = (isEdit = false) => (
    <form
      id={isEdit ? 'veh-edit-form' : 'veh-add-form'}
      onSubmit={isEdit ? handleEditSubmit : handleAddSubmit}
      className="veh-form"
    >
      <div className="veh-form-section-title">Company &amp; Card</div>
      <div className="veh-form-grid">
        {/* Company selector — only show on Add (edit keeps existing companyId) */}
        {!isEdit && (
          <div className="veh-form-group">
            <label className="veh-form-label">Company <span className="required">*</span></label>
            <select
              id="veh-company-select"
              className="veh-form-select"
              value={form.companyId}
              onChange={e => setField('companyId', e.target.value)}
              required
            >
              <option value="">— Select company —</option>
              {companies.map(c => (
                <option key={c.id} value={c.id}>{c.legalName}</option>
              ))}
            </select>
          </div>
        )}

        {/* Card selector */}
        <div className="veh-form-group">
          <label className="veh-form-label">Fuel Card <span className="required">*</span></label>
          <select
            id="veh-card-select"
            className="veh-form-select"
            value={form.cardId}
            onChange={e => setField('cardId', e.target.value)}
            required={!isEdit}
            disabled={!isEdit && !form.companyId}
          >
            <option value="">— Select card —</option>
            {unassignedCards.map(c => (
              <option key={c.id} value={c.id}>
                {c.cardReference} {c.maskedCardNumber ? `(${c.maskedCardNumber})` : ''}
              </option>
            ))}
            {isEdit && form.cardId && !unassignedCards.find(c => c.id === form.cardId) && (
              <option value={form.cardId}>
                {selectedVehicle?.cardReference || (selectedVehicle?.cards && selectedVehicle.cards[0]?.cardReference) || form.cardId}
                {selectedVehicle?.cards?.[0]?.maskedCardNumber ? ` (${selectedVehicle.cards[0].maskedCardNumber})` : ''} (Currently assigned)
              </option>
            )}
          </select>
          {!isEdit && !form.companyId && (
            <p className="veh-form-hint">Select a company first to see available cards.</p>
          )}
        </div>
      </div>

      <div className="veh-form-section-title" style={{ marginTop: 4 }}>Vehicle Info</div>
      <div className="veh-form-grid">
        {/* Plate Number */}
        <div className="veh-form-group">
          <label className="veh-form-label">Plate Number</label>
          <input
            id="veh-plate-input"
            type="text"
            className="veh-form-input"
            placeholder="e.g. LND-123-AB"
            value={form.plateNumber}
            onChange={e => setField('plateNumber', e.target.value)}
          />
        </div>

        {/* VIN */}
        <div className="veh-form-group">
          <label className="veh-form-label">VIN</label>
          <input
            id="veh-vin-input"
            type="text"
            className="veh-form-input"
            placeholder="e.g. 1A2B3C4D5E6F7G8H9"
            value={form.vin}
            onChange={e => setField('vin', e.target.value)}
          />
        </div>

        {/* Make */}
        <div className="veh-form-group">
          <label className="veh-form-label">Make</label>
          <select
            id="veh-make-select"
            className="veh-form-select"
            value={form.make}
            onChange={e => { setField('make', e.target.value); setField('model', ''); }}
          >
            <option value="">— Select make —</option>
            {MANUFACTURERS.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          {form.make === 'Others' && (
            <input
              type="text"
              className="veh-form-input"
              style={{ marginTop: 6 }}
              placeholder="Enter make"
              value={customMake}
              onChange={e => setCustomMake(e.target.value)}
            />
          )}
        </div>

        {/* Model */}
        <div className="veh-form-group">
          <label className="veh-form-label">Model</label>
          <select
            id="veh-model-select"
            className="veh-form-select"
            value={form.model}
            onChange={e => setField('model', e.target.value)}
            disabled={!form.make}
          >
            <option value="">— Select model —</option>
            {modelOptions.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          {form.model === 'Custom Model' && (
            <input
              type="text"
              className="veh-form-input"
              style={{ marginTop: 6 }}
              placeholder="Enter model"
              value={customModel}
              onChange={e => setCustomModel(e.target.value)}
            />
          )}
        </div>

        {/* Year */}
        <div className="veh-form-group">
          <label className="veh-form-label">Year</label>
          <select
            id="veh-year-select"
            className="veh-form-select"
            value={form.year}
            onChange={e => setField('year', Number(e.target.value))}
          >
            {yearOptions.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {/* Contract ID */}
        <div className="veh-form-group">
          <label className="veh-form-label">Contract ID</label>
          <input
            id="veh-contract-input"
            type="text"
            className="veh-form-input"
            placeholder="e.g. CONTRACT-001"
            value={form.contractId}
            onChange={e => setField('contractId', e.target.value)}
          />
        </div>

        {/* Department */}
        <div className="veh-form-group">
          <label className="veh-form-label">Department</label>
          <input
            id="veh-dept-input"
            type="text"
            className="veh-form-input"
            placeholder="e.g. Logistics"
            value={form.department}
            onChange={e => setField('department', e.target.value)}
          />
        </div>

        {/* Assigned Driver */}
        <div className="veh-form-group">
          <label className="veh-form-label">Assigned Driver</label>
          <input
            id="veh-driver-input"
            type="text"
            className="veh-form-input"
            placeholder="e.g. John Doe"
            value={form.assignedDriver}
            onChange={e => setField('assignedDriver', e.target.value)}
          />
        </div>

        {/* Tank Capacity */}
        <div className="veh-form-group">
          <label className="veh-form-label">Tank Capacity (L)</label>
          <input
            id="veh-tank-input"
            type="number"
            min={0}
            className="veh-form-input"
            placeholder="e.g. 70"
            value={form.tankCapacity || ''}
            onChange={e => setField('tankCapacity', Number(e.target.value))}
          />
        </div>

        {/* Base Odometer */}
        <div className="veh-form-group">
          <label className="veh-form-label">Base Odometer (km)</label>
          <input
            id="veh-odometer-input"
            type="number"
            min={0}
            className="veh-form-input"
            placeholder="e.g. 45000"
            value={form.baseOdometer || ''}
            onChange={e => setField('baseOdometer', Number(e.target.value))}
          />
        </div>

        {/* Fuel Baseline */}
        <div className="veh-form-group">
          <label className="veh-form-label">Fuel Baseline (L/100km)</label>
          <input
            id="veh-baseline-input"
            type="number"
            min={0}
            step={0.1}
            className="veh-form-input"
            placeholder="e.g. 10.5"
            value={form.fuelBaseline || ''}
            onChange={e => setField('fuelBaseline', Number(e.target.value))}
          />
        </div>
      </div>

      <div className="veh-form-section-title" style={{ marginTop: 4 }}>IoT / Tracker (Optional)</div>
      <div className="veh-form-grid">
        {/* IMEI */}
        <div className="veh-form-group">
          <label className="veh-form-label">IMEI</label>
          <input
            id="veh-imei-input"
            type="text"
            className="veh-form-input"
            placeholder="15-digit IMEI"
            value={form.imei}
            onChange={e => setField('imei', e.target.value)}
          />
        </div>

        {/* ICCID */}
        <div className="veh-form-group">
          <label className="veh-form-label">SIM ICCID</label>
          <input
            id="veh-iccid-input"
            type="text"
            className="veh-form-input"
            placeholder="SIM card ICCID"
            value={form.iccid}
            onChange={e => setField('iccid', e.target.value)}
          />
        </div>

        {/* Tracker Model */}
        <div className="veh-form-group">
          <label className="veh-form-label">Tracker Model</label>
          <input
            id="veh-tracker-input"
            type="text"
            className="veh-form-input"
            placeholder="e.g. JC100"
            value={form.trackerModel}
            onChange={e => setField('trackerModel', e.target.value)}
          />
        </div>

        {/* Virtual Fuel Tracking */}
        <div className="veh-form-group" style={{ justifyContent: 'center' }}>
          <label className="veh-form-label">Virtual Fuel Tracking</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, cursor: 'pointer' }}>
            <input
              id="veh-virtual-fuel-check"
              type="checkbox"
              checked={!!form.useVirtualFuelTracking}
              onChange={e => setField('useVirtualFuelTracking', e.target.checked)}
            />
            <span style={{ fontSize: 13, color: '#374151' }}>Enable GPS-based fuel tracking</span>
          </label>
        </div>
      </div>

      <div className="veh-modal-footer">
        <button
          type="button"
          className="veh-btn-cancel"
          onClick={() => { isEdit ? setShowEditModal(false) : setShowAddModal(false); resetForm(); }}
        >
          Cancel
        </button>
        <button
          id={isEdit ? 'veh-edit-submit-btn' : 'veh-add-submit-btn'}
          type="submit"
          className="veh-btn-submit"
          disabled={isSubmitting}
        >
          {isSubmitting && <span className="vehicle-spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />}
          {isEdit ? 'Save Changes' : 'Create Vehicle'}
        </button>
      </div>
    </form>
  );

  // ---- Render ----
  return (
    <div className="vehicle-management-page">
      {/* Header */}
      <div className="vehicle-header">
        <div className="vehicle-title">
          <Car size={28} />
          <div>
            <h1>Vehicle Management</h1>
            <p className="vehicle-subtitle">Create and manage fleet vehicles across all companies</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            id="veh-refresh-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '9px 14px', border: '1px solid #e5e7eb',
              borderRadius: 8, background: '#fff', cursor: 'pointer',
              fontSize: 13, color: '#374151', fontWeight: 500,
            }}
            onClick={() => loadVehicles(filterCompanyId)}
            disabled={isLoading}
          >
            <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            id="veh-add-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '9px 16px', border: 'none',
              borderRadius: 8, background: '#ff6b35', cursor: 'pointer',
              fontSize: 13, color: '#fff', fontWeight: 600,
            }}
            onClick={handleOpenAdd}
          >
            <Plus size={14} /> Add Vehicle
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="vehicle-stats">
        {[
          { label: 'Total Vehicles', value: totalVehicles, color: '#374151', bg: '#f9fafb' },
          { label: 'With IoT Device', value: withIot, color: '#1e40af', bg: '#eff6ff' },
          { label: 'With Fuel Card', value: withCard, color: '#166534', bg: '#f0fdf4' },
          { label: 'Companies', value: companies_count, color: '#7c3aed', bg: '#f5f3ff' },
        ].map(stat => (
          <div
            key={stat.label}
            style={{
              background: stat.bg,
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: '20px 24px',
            }}
          >
            <p style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 8px' }}>{stat.label}</p>
            <p style={{ fontSize: 28, fontWeight: 700, color: stat.color, margin: 0 }}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="vehicle-filters">
        <div className="vehicle-search-wrapper">
          <Search size={15} />
          <input
            id="veh-search-input"
            type="text"
            className="vehicle-search-input"
            placeholder="Search plate, driver, make, VIN, IMEI…"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
          />
        </div>
        <select
          id="veh-company-filter"
          className="vehicle-filter-select"
          value={filterCompanyId}
          onChange={e => setFilterCompanyId(e.target.value)}
        >
          <option value="">All Companies</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.legalName}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="vehicles-section">
        <div className="section-header">
          <h2>Vehicles ({filtered.length})</h2>
        </div>

        {error && (
          <div style={{ padding: '16px 24px', color: '#dc2626', fontSize: 13 }}>
            ⚠ {error}
          </div>
        )}

        <div className="vehicle-table-wrapper">
          {isLoading ? (
            <div className="vehicle-loading">
              <div className="vehicle-spinner" />
              <span>Loading vehicles…</span>
            </div>
          ) : paginated.length === 0 ? (
            <div className="vehicle-empty">
              <Car size={32} strokeWidth={1.2} />
              <span>{searchQuery ? 'No vehicles match your search.' : 'No vehicles found. Click "Add Vehicle" to create one.'}</span>
            </div>
          ) : (
            <table className="vehicle-table">
              <thead>
                <tr>
                  <th>Vehicle / Driver</th>
                  <th>Plate Number</th>
                  <th>Company</th>
                  <th>Fuel Card</th>
                  <th>IoT</th>
                  <th>Dept / Contract</th>
                  <th>Tank</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(v => (
                  <tr key={v.id}>
                    <td>
                      <div className="vehicle-cell-primary">
                        {[v.make, v.model, v.year].filter(Boolean).join(' ') || '—'}
                      </div>
                      <div className="vehicle-cell-secondary">
                        {v.assignedDriver || <span style={{ color: '#d1d5db' }}>No driver</span>}
                      </div>
                    </td>
                    <td>
                      {v.plateNumber
                        ? <span className="vehicle-plate-badge">{v.plateNumber}</span>
                        : <span style={{ color: '#d1d5db' }}>—</span>
                      }
                    </td>
                    <td>
                      <div className="vehicle-cell-primary" style={{ fontSize: 13 }}>
                        {v.company?.legalName
                          || companies.find(c => c.id === v.companyId)?.legalName
                          || <span style={{ color: '#d1d5db', fontStyle: 'italic', fontSize: 12 }}>{v.companyId ? 'Unknown company' : '—'}</span>
                        }
                      </div>
                      {v.companyId && (
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                          {v.companyId.slice(0, 8)}…
                        </div>
                      )}
                    </td>
                    <td>
                      {(() => {
                        const cardRef = v.cardReference || (v.cards && v.cards[0]?.cardReference);
                        const maskedNum = v.cards && v.cards[0]?.maskedCardNumber;
                        if (!cardRef) {
                          return <span style={{ color: '#d1d5db' }}>Unassigned</span>;
                        }
                        return (
                          <div>
                            <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600 }}>{cardRef}</span>
                            {maskedNum && (
                              <div style={{ fontSize: 11, color: '#6b7280' }}>{maskedNum}</div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      {v.imei && v.imei.trim() !== '' ? (
                        <span className="iot-badge connected">
                          <Wifi size={11} /> {v.imei.slice(0, 8)}…
                        </span>
                      ) : (
                        <span className="iot-badge none">
                          <WifiOff size={11} /> None
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: 12, color: '#374151' }}>{v.department || '—'}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{v.contractId || ''}</div>
                    </td>
                    <td>
                      <div style={{ fontSize: 12, color: '#374151' }}>
                        {v.tankCapacity ? `${v.tankCapacity} L` : '—'}
                      </div>
                    </td>
                    <td>
                      <ActionDropdown
                        vehicle={v}
                        onEdit={handleOpenEdit}
                        onDelete={handleOpenDelete}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {!isLoading && filtered.length > PAGE_SIZE && (
          <div className="vehicle-pagination">
            <div className="pagination-info">
              Showing <strong>{Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}</strong>–<strong>{Math.min(page * PAGE_SIZE, filtered.length)}</strong> of{' '}
              <strong>{filtered.length.toLocaleString()}</strong> vehicles
            </div>
            <div className="pagination-controls">
              <button
                id="veh-prev-page"
                className="pagination-nav-btn"
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
                <span>Prev</span>
              </button>

              <div className="pagination-pages">
                {getPaginationItems(page, totalPages).map((item, idx) =>
                  item === 'ellipsis' ? (
                    <span key={`ellipsis-${idx}`} className="pagination-ellipsis">
                      …
                    </span>
                  ) : (
                    <button
                      key={item}
                      id={`veh-page-${item}`}
                      className={`pagination-page-btn ${item === page ? 'active' : ''}`}
                      onClick={() => setPage(item)}
                    >
                      {item}
                    </button>
                  )
                )}
              </div>

              <button
                id="veh-next-page"
                className="pagination-nav-btn"
                disabled={page === totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                aria-label="Next page"
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ---- Add Modal ---- */}
      <Modal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); resetForm(); }}
        title="Add New Vehicle"
        size="lg"
      >
        {renderVehicleForm(false)}
      </Modal>

      {/* ---- Edit Modal ---- */}
      <Modal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedVehicle(null); resetForm(); }}
        title={`Edit Vehicle — ${selectedVehicle?.plateNumber || selectedVehicle?.vin || 'Vehicle'}`}
        size="lg"
      >
        {renderVehicleForm(true)}
      </Modal>

      {/* ---- Delete Confirmation Modal ---- */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setSelectedVehicle(null); }}
        title="Delete Vehicle"
        size="sm"
      >
        <div style={{ padding: '8px 0' }}>
          <p style={{ fontSize: 14, color: '#374151', margin: '0 0 8px' }}>
            Are you sure you want to permanently delete vehicle:
          </p>
          <p style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
            {[selectedVehicle?.plateNumber, selectedVehicle?.make, selectedVehicle?.model].filter(Boolean).join(' — ') || selectedVehicle?.id}
          </p>
          <p style={{ fontSize: 13, color: '#ef4444', margin: '8px 0 0' }}>
            This action cannot be undone. The fuel card will be unassigned.
          </p>
          <div className="veh-modal-footer">
            <button
              id="veh-delete-cancel-btn"
              className="veh-btn-cancel"
              onClick={() => { setShowDeleteModal(false); setSelectedVehicle(null); }}
            >
              Cancel
            </button>
            <button
              id="veh-delete-confirm-btn"
              className="veh-btn-delete"
              onClick={handleDeleteConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Deleting…' : 'Delete Vehicle'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default VehicleManagementPage;
